import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button } from '@mui/material';
import { onAutocompleteChangeInForm } from '@sharedCR/common/form';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { ConfirmationModalType } from 'constants/confirmationDialog';
import { CreditTypeEnum, PurchaseCreditSourceType } from 'constants/loanApplication';
import { HistoryPendingChangesActionType } from 'constants/pendingChanges';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';
import { mergePendingChanges } from 'utils/pendingChanges';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { openConfirmationDialogAction } from 'store/global/actions';
import {
  selectIsAdditionalLoanPurposeFromApplication,
  selectIsFHALoanType,
  selectIsHelocPurposeRefinanceFromPropertyAndLoanSummary,
  selectIsRefinanceLoanPurposeFromApplication,
} from 'store/loanApplications/selectors';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';

import { getCreditConfig } from './Credits.config';
import { controlsStyles, formContainerStyles } from './Credits.styles';

const defaultArray = [];

const CreditForm = ({
  creditData,
  isLoading,
  cancelForm,
  creditsList = defaultArray,
  pendingChanges,
  saveForm,
}) => {
  const dispatch = useDispatch();

  const hasUserPermissionToEditLenderCredit = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_EDIT_LENDER_CREDIT),
  );
  const isRefinanceFlow = useSelector(selectIsRefinanceLoanPurposeFromApplication);
  const isAdditionalLoan = useSelector(selectIsAdditionalLoanPurposeFromApplication);
  const isHelocPurposeRefinance = useSelector(
    selectIsHelocPurposeRefinanceFromPropertyAndLoanSummary,
  );
  const isFHA = useSelector(selectIsFHALoanType);

  const isFHARefinance =
    isFHA && (isRefinanceFlow || (isAdditionalLoan && isHelocPurposeRefinance));
  const observableFieldsNames = Object.keys(creditData);

  const { initialFormValues, fieldsConfig } = useMemo(() => {
    const config = getCreditConfig({
      isFHARefinance,
      hasUserPermissionToEditLenderCredit,
      creditsList,
      creditType: creditData.creditType,
    });

    const { initialFormValues, schema, fieldsConfig } = getConfigInfo({
      applicationData: creditData,
      config,
    });

    return { initialFormValues, schema, fieldsConfig };
  }, [creditData, creditsList, hasUserPermissionToEditLenderCredit, isFHARefinance]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    isFormChanged,
    validateField,
    validateForm,
    staticValidateField,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const onCreditTypeChange = useCallback(
    (e, option) => {
      const isNewPurchaseCreditType = option?.value === CreditTypeEnum.PURCHASE_CREDIT;
      const isNewOtherCreditType = option?.value === CreditTypeEnum.OTHER_CREDIT;

      onAutocompleteChangeInForm({ validateField, setInForm })(e, option);

      if (!isNewOtherCreditType) {
        setInForm('description', '', false);
      }

      if (!isNewPurchaseCreditType) {
        setInForm('purchaseCreditType', '', false);
      }

      if (!isNewPurchaseCreditType && !isNewOtherCreditType) {
        setInForm('sourceType', '', false);
      }
    },
    [setInForm, validateField],
  );

  const onSourceTypeChange = useCallback(
    (e, option) => {
      const isNewOtherSourceType = option?.value === PurchaseCreditSourceType.OTHER;

      onAutocompleteChangeInForm({ validateField, setInForm })(e, option);

      if (!isNewOtherSourceType) {
        setInForm('otherDescription', '', false);
      }
    },
    [setInForm, validateField],
  );

  const formConfig = useMemo(() => {
    const formConfig = getCreditConfig({
      isFHARefinance,
      hasUserPermissionToEditLenderCredit,
      onCreditTypeChange,
      onSourceTypeChange,
      creditsList,
      dataId: creditData.id,
      creditType: form.creditType?.value,
      sourceType: form.sourceType?.value,
      purchaseCreditType: form.purchaseCreditType?.value,
    });

    const { schema } = getConfigInfo({
      applicationData: form,
      config: formConfig,
    });
    setSchema(schema);

    return formConfig;
  }, [
    creditData.id,
    creditsList,
    form,
    hasUserPermissionToEditLenderCredit,
    isFHARefinance,
    onCreditTypeChange,
    onSourceTypeChange,
    setSchema,
  ]);

  const { mergedPendingChanges, isPendingChangesHighlightDisabled } = useMemo(() => {
    const mergedPendingChanges = mergePendingChanges(pendingChanges, formPendingChanges);

    return {
      mergedPendingChanges,
      isPendingChangesHighlightDisabled: Object.values(mergedPendingChanges).some(
        ({ actionType }) => actionType === HistoryPendingChangesActionType.ADDED,
      ),
    };
  }, [formPendingChanges, pendingChanges]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      saveForm({ updatedData: form, initialData: creditData });
    },
    [form, validateForm, saveForm, creditData],
  );

  const onCancel = useCallback(async () => {
    if (!isFormChanged) {
      cancelForm(creditData);

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Cancel Changes',
        description: 'Are you sure you want to cancel changes?',
        onAccept: () => {
          cancelForm(creditData);
        },
      }),
    );
  }, [cancelForm, creditData, dispatch, isFormChanged]);

  return (
    <form noValidate className="w-full" data-ischanged={isFormChanged} onSubmit={onSubmit}>
      <Box sx={formContainerStyles}>
        <FormGenerator
          config={formConfig}
          disabledPendingChangesHighlight={isPendingChangesHighlightDisabled}
          form={form}
          formName={FormNames.CREDITS}
          handleChange={handleChange}
          isLoading={isLoading}
          numberOfColumns={5}
          pendingChanges={mergedPendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
        <Box sx={controlsStyles}>
          <Button
            data-testid={PropertyAndLoanTestIds.CREDIT_FORM_CANCEL_BUTTON}
            size="small"
            variant="outlined"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            data-testid={PropertyAndLoanTestIds.CREDIT_FORM_SUBMIT_BUTTON}
            disabled={!isFormChanged}
            size="small"
            type="submit"
          >
            Save
          </Button>
        </Box>
      </Box>
    </form>
  );
};

CreditForm.propTypes = {
  creditData: PropTypes.object.isRequired,
  cancelForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  pendingChanges: PropTypes.object,
  creditsList: PropTypes.array,
  ...WithLoadingPropTypes,
};

export default memo(CreditForm);
